// CabinetDossierPage — ÉCRAN 7 de la maquette : « Mon dossier » (v5.20.0).
// Onglets Vue d'ensemble | Documents | Rendez-vous | Messages ·
// Prochain RDV · Timeline · Documents · Messages · Factures & paiements.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Video } from "lucide-react";
import { T, Avatar } from "@/components/cabinet/cabinetTheme";
import { DossierMessages, DossierDocuments, DossierReview } from "@/components/cabinet/DossierPanels";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_FR = { scheduled: "Confirmé", waiting: "En attente", in_progress: "En cours", completed: "Terminé", cancelled: "Annulé", new: "Envoyée", assigned: "En cours d'analyse", closed: "Clôturée" };
const TABS = [["overview", "Vue d'ensemble"], ["documents", "Documents"], ["rdv", "Rendez-vous"], ["messages", "Messages"]];

export default function CabinetDossierPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    const token = localStorage.getItem("civ_user_token");
    if (!token) { setErr("auth"); return; }
    axios.get(`${API}/api/v3/cabinet/me/dossier`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setDossier(r.data.dossier))
      .catch((e) => setErr(e.response?.status === 401 ? "auth" : "load"));
  }, []);

  if (err === "auth") return (
    <div style={{ ...T.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }} data-testid="dossier-auth-required">
      <div style={T.sub}>Connectez-vous avec votre compte AYA pour voir votre dossier.</div>
      <button style={T.btnGreen} onClick={() => navigate(`/login?next=/cabinet/${slug}/dossier`)} data-testid="dossier-login-btn">Se connecter</button>
    </div>
  );
  if (err) return <div style={{ ...T.page, display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>Erreur de chargement.</div>;
  if (!dossier) return <div style={{ ...T.page, display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>Chargement…</div>;

  const next = dossier.next_appointment;
  const cabName = next?.cabinet?.name || dossier.requests[0]?.cabinet?.name || "Cabinet";

  // Timeline du dossier (maquette : étapes cochées vertes + "À venir" grise).
  const timeline = [
    ...dossier.requests.slice(0, 1).flatMap((r) => [
      { label: "Demande envoyée", at: r.created_at, done: true },
      { label: "En cours d'analyse", at: r.updated_at || r.created_at, done: ["assigned", "in_progress", "closed"].includes(r.status) },
    ]),
    ...(next ? [{ label: "Rendez-vous confirmé", at: next.created_at, done: true }] : []),
    { label: "Consultation effectuée", at: null, done: dossier.appointments.some((a) => a.status === "completed") },
  ];

  const NextCard = () => next ? (
    <>
      {next.reminder_due && (
        <div style={{ ...T.card, border: "1.5px solid rgba(249,115,22,0.5)", background: "#FFF9F3", display: "flex", gap: 10, alignItems: "center" }} data-testid="dossier-reminder-banner">
          <span style={{ fontSize: 20 }}>⏰</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 900, color: "#C2410C" }}>Votre rendez-vous commence bientôt</div>
            <div style={{ ...T.sub, fontSize: 11.5 }}>Aujourd'hui à {next.time} avec {next.member?.name}</div>
          </div>
          {next.mode === "visio" && (
            <button style={{ ...T.btnOrange, padding: "8px 13px", fontSize: 11.5 }} data-testid="reminder-join-visio"
              onClick={() => navigate(`/cabinet/${next.cabinet?.slug || slug}/visio/${next.appointment_id}`)}>Rejoindre</button>
          )}
        </div>
      )}
    <div style={T.card} data-testid="dossier-next-appt">
      <div style={{ fontSize: 12, fontWeight: 800, color: "#374151" }}>Prochain rendez-vous</div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: "#DBEAFE", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>📹</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 900 }}>{next.mode === "visio" ? "Visio" : "RDV"} avec {next.member?.name}</div>
          <div style={{ ...T.sub, fontSize: 11.5 }}>{next.date} · {next.time}<br />{next.duration_min} min · Orientation</div>
        </div>
      </div>
      {next.mode === "visio" && (
        <button style={{ ...T.btnGreen, width: "100%", marginTop: 11, padding: "10px 0" }} data-testid="dossier-join-visio"
          onClick={() => navigate(`/cabinet/${next.cabinet?.slug || slug}/visio/${next.appointment_id}`)}>
          <Video size={14} /> Rejoindre la visio
        </button>
      )}
    </div>
    </>
  ) : (
    <div style={{ ...T.card, ...T.sub }} data-testid="dossier-no-appt">
      Aucun rendez-vous à venir. <span style={{ color: "#0E8A4F", fontWeight: 800, cursor: "pointer" }} onClick={() => navigate(`/cabinet/${slug}`)}>Prendre rendez-vous →</span>
    </div>
  );

  const Timeline = () => (
    <div style={T.card} data-testid="dossier-history">
      <div style={{ display: "grid", gap: 0 }}>
        {timeline.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", paddingBottom: i < timeline.length - 1 ? 14 : 0, position: "relative" }}>
            <div style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", background: s.done ? "#0E8A4F" : "#D1D5DB" }}>{s.done ? "✓" : ""}</div>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: s.done ? "#111827" : "#9CA3AF" }}>{s.label}</div>
              <div style={{ ...T.sub, fontSize: 11 }}>{s.at ? String(s.at).slice(0, 16).replace("T", " · ") : "À venir"}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const MessagesPreview = () => (
    <div style={{ ...T.card, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setTab("messages")} role="button" data-testid="dossier-messages-preview">
      <div>
        <h2 style={T.h2}>Messages</h2>
        <div style={{ ...T.sub, fontSize: 12, marginTop: 3 }}>
          {dossier.unread_messages > 0 ? `${dossier.unread_messages} nouveau(x) message(s) du cabinet` : "Échangez avec le cabinet."}
        </div>
      </div>
      <span style={{ color: "#0E8A4F", fontSize: 12, fontWeight: 800 }}>Ouvrir →</span>
    </div>
  );

  const RdvList = () => (
    <div style={T.card} data-testid="dossier-rdv-list">
      {dossier.appointments.map((a) => (
        <div key={a.appointment_id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 0", borderBottom: "1px solid #F3F4F6" }}>
          <Avatar name={a.member?.name} size={32} color="#065F46" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>{a.mode === "visio" ? "📹 Visio" : "RDV"} — {a.member?.name}</div>
            <div style={{ ...T.sub, fontSize: 11 }}>{a.date} · {a.time} · {a.cabinet?.name}</div>
          </div>
          <span style={{ ...T.badge, ...T.badgeGreen }}>{STATUS_FR[a.status] || a.status}</span>
        </div>
      ))}
      {dossier.appointments.length === 0 && <div style={T.sub}>Aucun rendez-vous.</div>}
    </div>
  );

  const Factures = () => (
    <div style={T.card} data-testid="dossier-invoices">
      <h2 style={T.h2}>Factures & Paiements</h2>
      {dossier.appointments.slice(0, 1).map((a) => (
        <div key={a.appointment_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 9 }}>
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}>Consultation d'orientation</div>
            <div style={{ ...T.sub, fontSize: 11.5 }}>Gratuit</div>
          </div>
          <span style={{ ...T.badge, ...T.badgeGreen }}>Payé</span>
        </div>
      ))}
      {dossier.appointments.length === 0 && <div style={{ ...T.sub, marginTop: 8 }}>Aucune facture.</div>}
    </div>
  );

  return (
    <div style={T.page} data-testid="dossier-page">
      <div style={{ maxWidth: 640, margin: "0 auto", padding: 16, display: "grid", gap: 12 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={() => navigate(`/cabinet/${slug}`)} style={T.back} data-testid="dossier-back" aria-label="Retour">←</button>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚖️</div>
          <div>
            <h1 style={{ ...T.h1, fontSize: 17 }}>Mon dossier</h1>
            <div style={{ ...T.sub, fontSize: 12 }}>{cabName}</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 16, borderBottom: "1px solid #E5E7EB", overflowX: "auto", scrollbarWidth: "none" }} data-testid="dossier-tabs">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} style={{ ...T.tab, ...(tab === v ? T.tabOn : {}), position: "relative" }} data-testid={`dossier-tab-${v}`}>
              {l}
              {v === "messages" && dossier.unread_messages > 0 && (
                <span style={{ marginLeft: 5, display: "inline-block", minWidth: 15, padding: "0 4px", borderRadius: 99, background: "#F97316", color: "#fff", fontSize: 9.5, fontWeight: 900 }} data-testid="dossier-unread-badge">{dossier.unread_messages}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "overview" && <><NextCard /><DossierReview slug={slug} cabinetName={cabName} /><Timeline /><DossierDocuments slug={slug} /><MessagesPreview /><Factures /></>}
        {tab === "documents" && <DossierDocuments slug={slug} />}
        {tab === "rdv" && <><NextCard /><RdvList /></>}
        {tab === "messages" && <DossierMessages slug={slug} cabinetName={cabName} />}
      </div>
    </div>
  );
}
